import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Upload, ExternalLink, Star, Trash2 } from "lucide-react";
import {
  listJobDocuments,
  uploadJobDocument,
  patchJobDocument,
  deleteJobDocument,
  resolveFileUrl,
} from "@/lib/jobsApi";
import ErrorState from "@/pages/jobs/ErrorState";

/**
 * Iter-267 §2: Applicant document library rendered as a section inside
 * the Applicant Profile screen. Same API surface, same test-ids as the
 * legacy stand-alone `/jobs/documents` page so the wizard's link and
 * every automated flow keeps working.
 */
const OK_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export default function DocumentLibrary({ heading = "Document library", subheading }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["jobs-documents"], queryFn: listJobDocuments });
  const [docType, setDocType] = useState("resume");
  const [busy, setBusy] = useState(false);

  const uploadMut = useMutation({
    mutationFn: uploadJobDocument,
    onSuccess: () => {
      toast.success("Document uploaded.");
      qc.invalidateQueries({ queryKey: ["jobs-documents"] });
    },
    onError: (err) => {
      const s = err?.response?.status;
      toast.error(
        s === 415
          ? "Only PDF/DOC/DOCX allowed."
          : s === 413
            ? "Max 5 MB."
            : (err?.response?.data?.detail || "Upload failed"),
      );
    },
  });
  const patchMut = useMutation({
    mutationFn: (id) => patchJobDocument(id, { is_default: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["jobs-documents"] }),
  });
  const delMut = useMutation({
    mutationFn: deleteJobDocument,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["jobs-documents"] }),
  });

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!OK_TYPES.includes(file.type)) {
      toast.error("Only PDF/DOC/DOCX allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max 5 MB.");
      return;
    }
    setBusy(true);
    const reader = new FileReader();
    reader.onload = () => {
      uploadMut.mutate({
        file_name: file.name,
        file_data: reader.result,
        doc_type: docType,
      });
      setBusy(false);
      e.target.value = "";
    };
    reader.readAsDataURL(file);
  };

  const docs = q.data?.documents || [];
  return (
    <section className="mt-8" data-testid="jobs-documents-section">
      <h2 className="font-display text-xl font-black text-slate-900" data-testid="jobs-documents-title">
        {heading}
      </h2>
      {subheading && <p className="text-sm text-slate-500 mt-1">{subheading}</p>}

      <div className="mt-4 rounded-2xl bg-white border border-soft p-5">
        <div className="flex flex-wrap items-center gap-3">
          <select
            data-testid="doc-type-select"
            className="input w-auto"
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
          >
            <option value="resume">Resume / CV</option>
            <option value="cover_letter">Cover letter</option>
          </select>
          <label className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-5 py-2.5 cursor-pointer">
            {busy || uploadMut.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            Upload document
            <input
              data-testid="doc-upload-input"
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={onFile}
            />
          </label>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          PDF / DOC / DOCX up to 5 MB. First document of each type becomes your default.
        </p>
      </div>

      <ul className="mt-4 space-y-2" data-testid="jobs-documents-list">
        {q.isLoading && (
          <li className="text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin inline" /> Loading…
          </li>
        )}
        {q.isError && (
          <li>
            <ErrorState onRetry={() => q.refetch()} />
          </li>
        )}
        {!q.isLoading && !q.isError && !docs.length && (
          <li className="text-slate-500 text-sm">No documents yet.</li>
        )}
        {docs.map((d) => (
          <li
            key={d.id}
            data-testid={`doc-row-${d.id}`}
            className="flex items-center gap-3 rounded-xl bg-white border border-soft px-4 py-3"
          >
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-800 truncate flex items-center gap-2">
                {d.file_name}
                {d.is_default && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
                    <Star className="w-3 h-3 fill-emerald-600" />
                    Default
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                {d.doc_type === "resume" ? "Resume" : "Cover letter"} ·{" "}
                {new Date(d.uploaded_at).toLocaleDateString("en-NZ")}
              </div>
            </div>
            <a
              href={resolveFileUrl(d.file_url)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`doc-open-${d.id}`}
              className="text-slate-500 hover:text-[color:var(--brand-blue)]"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
            {!d.is_default && (
              <button
                onClick={() => patchMut.mutate(d.id)}
                data-testid={`doc-default-${d.id}`}
                className="text-xs font-semibold text-[color:var(--brand-blue)] hover:underline"
              >
                Set default
              </button>
            )}
            <button
              onClick={() => delMut.mutate(d.id)}
              data-testid={`doc-delete-${d.id}`}
              className="text-red-500 hover:text-red-600"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
